/* eslint-disable */
import './B2BElementsExplorer.css';

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Pagination, PaginationItem } from '@mui/material';
import { generateClassName } from './helpers';
import useB2BTranslation from './hooks/useB2BTranslation';
import { ArrowCircleLeftOutlined, ArrowCircleRightOutlined } from '@mui/icons-material';
import CustomButton from './CustomButton';

export const getNestedProperty = (obj, path) => {
    if (typeof path !== 'string') {
        return undefined;
    }
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const B2BElementsExplorer = ({
    title = null, // This is the title of the explorer
    isSearching = false, // This is a flag to indicate if the component is searching for elements
    elementsData, // This is the data that will be used to render the elements
    pagesCount,
    currentPage,
    handlePageChange,
    pageNumberLoading,
    className = null,
    ElementComponent, // This is the component that will be used to render each element
    elementComponentPropName = 'element', // This is the name of the prop that will be passed to the ElementComponent
    otherComponentProps = {}, // These are the other props that will be passed to the ElementComponent
    elementComponentHandlers = {}, // These are the handlers that will be passed to the ElementComponent
    elementIDPropName = null, // This is the name of the prop that will be used to identify each element
    goToID = null, // This is the ID of the element that should be scrolled to
    paginationStyle = "standard", // This is the style of the pagination
    noResultsMessage = null, // This is the message that will be displayed when there are no results
}) => {
    const { t } = useB2BTranslation('b2b-common');
    const elementRefs = useRef({});
    const [highlightedElementID, setHighlightedElementID] = useState(goToID);
    const [requestedGoToID, setRequestedGoToID] = useState(goToID);

    const [currentElementsData, setCurrentElementsData] = useState(elementsData);
    useEffect(() => {
        if (elementsData !== currentElementsData) {
            setCurrentElementsData(elementsData);
        }
    }, [currentElementsData, elementsData]);

    useEffect(() => {
        if (goToID !== requestedGoToID) {
            setRequestedGoToID(goToID);
        }
    }, [goToID, requestedGoToID]);

    useEffect(() => {
        if (requestedGoToID && elementRefs.current[requestedGoToID]) {
            const scrollTimeout = setTimeout(() => {
                // Double-check that the element still exists before scrolling
                if (elementRefs.current[requestedGoToID]) {
                    elementRefs.current[requestedGoToID].scrollIntoView({ behavior: 'smooth', block: 'end' });
                    setHighlightedElementID(requestedGoToID);

                    const highlightTimeout = setTimeout(() => {
                        setHighlightedElementID(null);
                    }, 15000);

                    return () => clearTimeout(highlightTimeout);
                }
            }, 250); // Delay before scrolling to the element

            return () => clearTimeout(scrollTimeout);
        }
    }, [requestedGoToID, elementRefs]);

    const noData = !currentElementsData || !currentElementsData.content || currentElementsData.content.length === 0;
    const isEdgeNavigation = paginationStyle === "edge-navigation";

    return (
        <div className={generateClassName("explorer-results", "-container", className)}>
            {title && <h2>{title}</h2>}
            <div className={`${generateClassName("explorer-results", "-elements", className)}${isSearching?' searching':''}${isEdgeNavigation?' edge-navigation':''}`}>
                {isSearching ? (
                    <CircularProgress />
                ) : (
                    (noData) ? (
                            (noResultsMessage) ? (<p>{noResultsMessage}</p>) : (  
                                <p>{t('No results found')}</p>
                        )
                    ) : (
                        currentElementsData.content.map((element, index) => {
                            // console.log('element:', element);
                            const elementID = elementIDPropName ? getNestedProperty(element, elementIDPropName) : null;
                            // console.log('elementID:', elementID);
                            const isHighlighted = elementIDPropName ? (elementID === highlightedElementID) : false;
                            return (
                                <div
                                    key={index}
                                    ref={el => elementRefs.current[elementID] = el}
                                    className={isHighlighted ? generateClassName('explorer-results', '-element-highlighted', null) :
                                        generateClassName('explorer-results', '-element', null)}
                                >
                                    <ElementComponent {...{ [elementComponentPropName]: element }} {...otherComponentProps} {...elementComponentHandlers} />
                                </div>
                            );
                        })
                    )
                )}
                {paginationStyle === "edge-navigation" && pagesCount > 1 && (currentPage !== 1) && (
                    <CustomButton
                        label={null}
                        tooltip={t('Previous page')}
                        icon={<ArrowCircleLeftOutlined />}
                        className={`${generateClassName("explorer-results", "-edge-navigation-prev-button", className)} ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(null, currentPage - 1)}
                        disabled={currentPage === 1}
                    />
                )}
                {paginationStyle === "edge-navigation" && pagesCount > 1 && (currentPage !== pagesCount) && (
                    <CustomButton
                        label={null}
                        tooltip={t('Next page')}
                        icon={<ArrowCircleRightOutlined />}
                        className={`${generateClassName("explorer-results", "-edge-navigation-next-button", className)} ${currentPage === pagesCount ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(null, currentPage + 1)}
                        disabled={currentPage === pagesCount}
                    />
                )}
            </div>
            {paginationStyle === "standard" && pagesCount > 1 && (
                <Pagination
                    count={pagesCount}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    className={generateClassName("explorer-results", "-pagination", className)}
                    renderItem={(item) => {
                        const toBeDisabled = (item.type === 'previous' && currentPage === 1) || (item.type === 'next' && currentPage === pagesCount);
                        const modifiedItem = {
                            ...item,
                            className: generateClassName("explorer-results", "-pagination-item", className),
                            disabled: toBeDisabled,
                            page: pageNumberLoading === item.page ? '...' : item.page,
                        };
                        return <PaginationItem {...modifiedItem} />;
                    }}
                />
            )}
        </div>
    );
};

B2BElementsExplorer.propTypes = {
    elementsData: PropTypes.shape({
        content: PropTypes.array,
    }),
};

export default B2BElementsExplorer;